<script setup lang="ts">
import {Ref, ref} from "vue";
import WidgetContainer from "@/components/EditView/widgets/WidgetContainer.vue";
import {WidgetProps} from "@/utils/presenters/interfaces.ts";
import ErrorTooltip from "@/components/EditView/ErrorTooltip.vue";
import {QUploader} from "quasar";
import FileUploaderWidgetUtil from "@/utils/editView/widgets/FileUploaderWidgetUtil.ts";
import AttachmentIcon from "@/assets/widgetIcons/AttachmentIcon.vue";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import AddRecordIcon from "@/assets/editViewIcons/AddRecordIcon.vue";

let props = defineProps<WidgetProps>();
const fieldId = props.field.id!;
let fieldUtil = new FileUploaderWidgetUtil(props.presenter, props.fieldMetaData, fieldId, undefined, props.field);
const url = fieldUtil.computedUrl();
const uploader: Ref<QUploader | undefined> = ref();
let loadingStore = useLoadingStore();

function getUrl(files: any) {
  if (!files || !files[0])
    return "";
  return `upload`;
}


async function startUpload(e: MouseEvent) {
  if (uploader.value) {
    uploader.value.pickFiles(e);
  }
}

async function startScan() {
  let scannerApp: string | undefined = ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.defaultScannerApp;
  let profileNumber: Number = FileUploaderWidgetUtil.getProfileNumber();
  let page: string = "cScanTwain/index.html?";
  let h: number = window.screen.height;
  let w: Number = window.screen.width;
  if (scannerApp === "DynamicWebTWAIN")
    page = "dynamicWebTWAIN/index.html?";
  let params: string = "profile=" + profileNumber + "&";
  window.onScannerOK = async () => {
    await fieldUtil.onDataReadyFromScannerOrSignature(window.dataFromScanner);
  }
  window.open(page + params, "scanner", "height=" + h + ",width=" + w);
}

async function onScanClick(e: MouseEvent) {
  if (fieldUtil.isFieldDisabled(props.forceEnableField))
    return;
  if (e.altKey || e.ctrlKey) {
    await startUpload(e);
  } else {
    await startScan();
  }
}

async function onAttachmentClick(e: MouseEvent) {
  if (fieldUtil.isFieldDisabled(props.forceEnableField))
    return;
  if (e.altKey || e.ctrlKey)
    await startScan();
  else
    await startUpload(e);
}
</script>

<template>
  <WidgetContainer class="tw-h-full"
                   :field="field"
                   :entityType="presenter.entityType"
                   :isFieldDisabled="fieldUtil.isFieldDisabled(forceEnableField)"
                   :isFieldRequired="fieldUtil.isFieldRequired()" :isSpanned="isSpanned"
                   :doNotApplyThemeLayouts="doNotApplyThemeLayouts" :isInsideComposite="isInsideComposite" title="&#8203;">
    <template v-slot:widget>
      <q-uploader
        ref="uploader"
        :id="field.id"
        auto-upload dense hide-bottom-space
        :readonly="fieldUtil.isFieldDisabled(forceEnableField)"
        @rejected="fieldUtil.onRejected"
        :url="getUrl"
        @start="loadingStore.startLoading()"
        @finish="loadingStore.stopLoading()"
        @uploaded="fieldUtil.onPartialUserInput"
        :multiple="false"
        :class="{'nama-mainFile-disabled':fieldUtil.isFieldDisabled(forceEnableField),'nama-mainFile':!fieldUtil.isFieldDisabled(forceEnableField)}">
        <template #header>
          <div class="tw-px-1 tw-items-center tw-place-self-start tw-justify-between tw-flex tw-flex-row tw-gap-1">
            <q-avatar square @click="onAttachmentClick"
                      class="nama-avatar-hover tw-cursor-pointer">
              <q-uploader-add-trigger v-if="fieldUtil.isAttachmentExist()  && !fieldUtil.isFieldDisabled(forceEnableField)"
                                      title="Upload Attachment (Alt Click for Scanner)"/>
              <AttachmentIcon class="tw-h-5 nama-widget-icon-hover nama-widget-icon"/>
            </q-avatar>
            <div v-if="fieldUtil.isAttachmentExist()" class="tw-flex tw-items-center tw-gap-2">
              <a :href="url">
                <q-chip
                  removable
                  @remove="fieldUtil.removeFile(forceEnableField)"
                  clickable
                  square
                  class="nama-chip"
                  :label="fieldUtil.getFileName()"
                  :title="fieldUtil.getFileName()">
                </q-chip>
              </a>
            </div>
          </div>
        </template>
        <template #list>

          <div v-if="url" class="tw-h-full">
            <img class="tw-rounded tw-h-fit tw-max-h-[350px]" alt="" :src="url">
          </div>
          <div v-else class="tw-h-32 tw-mb-1">
            <q-avatar square @click="onAttachmentClick"
                      class="tw-w-[95%] tw-h-[95%] hover:tw-bg-[#ECF0F7] hover:tw-rounded dark:hover:tw-bg-namaGray tw-cursor-pointer">
              <q-uploader-add-trigger
                v-if="!fieldUtil.isAttachmentExist() && !fieldUtil.eSignature && !fieldUtil.scanByDefault && !fieldUtil.isFieldDisabled(forceEnableField)"/>
              <AddRecordIcon class="tw-size-10  tw-py-1 !tw-fill-gray-400 nama-widget-icon-hover nama-widget-icon"/>
          </q-avatar>
          </div>
        </template>
        <ErrorTooltip v-if="presenter.fetchFieldErrorMsg(fieldId)" :error-msg="presenter.fetchFieldErrorMsg(fieldId)"/>
      </q-uploader>
    </template>
  </WidgetContainer>
</template>

<style>

</style>
